import React, { useState, useMemo } from "react";
import type {
  StepMap,
  StepRenderProps,
} from "@/components/onboarding/components/welcome-wizard/onboarding-steps.types";
import {
  FooterButton,
  FooterContainer,
  StepTitle,
  StepContent,
  CheckableOption,
  OptionsSection,
  OtherInput,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard/onboarding-steps.styles";
import { observer } from "mobx-react-lite";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { posthogClientManager } from "@/manager/posthog-client";
import { MDSIcon, MDSIconColor, MDSIconKind, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";

interface IcpRfiOption {
  id: string;
  title: string;
}

const ProfileOptions: IcpRfiOption[] = [
  { id: "own-business", title: "I run my own business or practice" },
  { id: "consultant", title: "I'm a consultant, coach, or advisor" },
  { id: "sales", title: "I'm in sales or business development" },
  { id: "project-manager", title: "I manage projects or operations" },
  { id: "team-lead", title: "I lead a team or department" },
  { id: "executive", title: "I'm an executive (C-suite / VP)" },
  { id: "other", title: "Other" },
];

const randomizeProfileOptions = (options: IcpRfiOption[]): IcpRfiOption[] => {
  const otherOption = options.find(option => option.id === "other");
  const rest = options.filter(option => option.id !== "other");

  const shuffled = [...rest];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return otherOption ? [...shuffled, otherOption] : shuffled;
};

export const IcpRfiStep: React.FC<StepRenderProps> = observer(({ onComplete }) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [otherText, setOtherText] = useState("");
  const randomizedOptions = useMemo(() => randomizeProfileOptions(ProfileOptions), []);

  const handleOptionSelect = (optionId: string) => {
    if (selectedOption === optionId) return;
    setSelectedOption(optionId);
    if (optionId !== "other") {
      setOtherText("");
    }
  };

  const handleNext = async () => {
    if (!selectedOption) return;

    const selectedOptionTitle = ProfileOptions.find(opt => opt.id === selectedOption)?.title;
    const properties = {
      selectedOption,
      selectedOptionTitle,
      otherText: selectedOption === "other" ? otherText.trim() : undefined,
    };
    trackEvent(TrackedEvent.IcpRfiStepComplete, properties);

    const posthogUserProperties = {
      icp_profile_option: selectedOption,
      icp_profile_label: selectedOptionTitle,
      icp_profile_other_text: selectedOption === "other" ? otherText.trim() : undefined,
      icp_profile_completed: true,
      icp_profile_completed_at: new Date().toISOString(),
    };

    const posthogIcpOnceProperties = {
      initial_icp_profile_completed_at: new Date().toISOString(),
    };

    posthogClientManager.setPersonProperties(posthogUserProperties, posthogIcpOnceProperties);

    onComplete("user-profile");
  };

  const isNextEnabled =
    selectedOption && (selectedOption !== "other" || otherText.trim().length > 0);

  return (
    <WizardContentWrapper>
      <StepTitle>What best describes how you work?</StepTitle>

      <StepContent overflowY="auto">
        <OptionsSection>
          {randomizedOptions.map(option => (
            <CheckableOption
              key={option.id}
              isSelected={selectedOption === option.id}
              onClick={() => handleOptionSelect(option.id)}
              isOtherOption={option.id === "other"}
              hasOtherText={option.id === "other" && otherText.trim().length > 0}
            >
              {option.id === "other" && selectedOption === "other" ? (
                <OtherInput
                  placeholder="Describe your role"
                  value={otherText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOtherText(e.target.value)
                  }
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
                  onKeyUp={(e: React.KeyboardEvent) => e.stopPropagation()}
                  autoFocus
                />
              ) : (
                <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
                  {option.title}
                </MDSText>
              )}
              {selectedOption === option.id &&
                (option.id !== "other" || otherText.trim().length > 0) && (
                  <MDSIcon
                    kind={MDSIconKind.CheckCircleSolid}
                    size={16}
                    color={MDSIconColor.Emphasis}
                  />
                )}
            </CheckableOption>
          ))}
        </OptionsSection>
      </StepContent>

      <FooterContainer>
        <FooterButton onClick={handleNext} isDisabled={!isNextEnabled}>
          Next
        </FooterButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
});

export const IcpRfiStepsMap: StepMap = {
  "user-profile": {
    id: "user-profile",
    render: props => <IcpRfiStep {...props} />,
  },
} as const;
